import { api } from './api';

/** Этап 1.2б: кабинет поставщика на выезде, листинг для сайта, цены по каналам, одобрение, договор, интеграция с сайтом. */
export type PartnerFull = {
  id: string;
  name: string;
  shortName: string | null;
  contact: string | null;
  role: string | null;
  phone: string | null;
  email: string | null;
  whatsapp: string | null;
  city: string | null;
  warehouse: string | null;
  address: string | null;
  pickupHours: string | null;
  trn: string | null;
  websiteUrl: string | null;
  categories: string[];
  notes: string | null;
  discount: number;
  noDiscountOnSpecial: boolean;
  markup: number | null;
  markupB2b: number | null;
  brandVisible: boolean;
  deliveryDaysMin: number | null;
  deliveryDaysMax: number | null;
  warrantyMonths: number | null;
  reservationPeriodDays: number;
  reservationRespondHours: number;
  poConfirmHours: number;
  paymentLagDays: number;
  minResaleType: string;
  minResaleValue: number | null;
  status: 'Active' | 'Paused' | 'Negotiating' | 'Onboarding';
  contractStatus: 'Draft' | 'Sent' | 'Signed' | 'Expired';
  contractStart: string | null;
  contractEnd: string | null;
  logoPath: string | null;
  onboardingStartedAt: string | null;
  skus?: number;
  lastPriceListVersion?: number | null;
  lastPriceListAt?: string | null;
};

export type Localized = { en: string | null; ar: string | null };
export type Availability = {
  sku: string;
  state: 'in_stock' | 'low_stock' | 'out_of_stock' | 'in_transit' | 'discontinued';
  qty: number;
  low_threshold: number;
  in_transit: { qty: number; eta: string | null; eta_confidence: string } | null;
  site_action: 'show' | 'preorder' | 'hide';
  reason: string | null;
  as_of: string;
  data_age_days: number;
};
export type SiteListing = {
  sku: string;
  listing_id: string;
  version: number;
  status: 'active' | 'draft' | 'archived';
  slug: string;
  name: Localized;
  short_description: Localized;
  description_html: Localized;
  features: { en: string[]; ar: string[] | null };
  specifications: { en: Record<string, string>; ar: Record<string, string> | null };
  brand: string | null;
  color: string | null;
  category: { primary: string | null; additional: string[] };
  group: { key: string; variant_label: Localized; sort_order: number } | null;
  images: { image_id: string; url: string; sha256: string; alt: Localized; sort_order: number; is_primary: boolean }[];
  prices: { currency: 'AED'; vat_excluded: true; b2c: number | null; b2b: number | null; sale: number | null; on_sale: boolean; min_resale: number | null };
  availability: Availability;
  logistics: { delivery_days_min: number | null; delivery_days_max: number | null; warranty_months: number | null; made_to_order: boolean };
  weight_kg: number | null;
  seo: { title: Localized; description: Localized };
  published_at: string | null;
  updated_at: string;
};
export type ChannelPrices = {
  cost: number | null;
  markupB2c: number;
  markupB2b: number;
  minResale: number | null;
  calcB2c: number | null;
  calcB2b: number | null;
  b2c: number | null;
  b2b: number | null;
  b2cManual: boolean;
  b2bManual: boolean;
  markupB2cActual: number | null;
  markupB2bActual: number | null;
  marginB2c: number | null;
  rrp: number | null;
  rrpMarkup: number | null;
  b2cVsRrp: number | null;
  belowMin: boolean;
};
export type ListingImage = { id: string; file: string; path: string; sha256: string; alt: string; sort_order: number; is_primary: boolean };
export type ListingStatus = 'none' | 'draft' | 'approved' | 'queued' | 'live' | 'failed' | 'archived';
export type ListingRow = {
  listing: SiteListing;
  prices: ChannelPrices;
  listingStatus: ListingStatus;
  listingVersion: number;
  listedOnSite: boolean;
  siteUrl: string | null;
  siteProductId: string | null;
  siteSyncedAt: string | null;
  siteActionOverride: 'show' | 'preorder' | 'hide' | null;
  problems: string[];
  supplierArticle: string | null;
  productStatus: string;
  images: ListingImage[];
  excludedByPartner: boolean;
};
export type Approval = {
  id: string;
  partnerId: string;
  priceListVersionId: string | null;
  approvedByName: string;
  approvedByContact: string | null;
  method: string;
  hasSignature: boolean;
  excludedSkus: string[];
  approvedSkusCount: number;
  notes: string | null;
  approvedAt: string;
};
export type ListingsPayload = {
  partner: PartnerFull;
  rows: ListingRow[];
  summary: {
    contractOk: boolean;
    contractStatus: string;
    approval: Approval | null;
    counts: Record<string, number>;
    ready: number;
    siteConfigured: boolean;
    markupB2c: number;
    markupB2b: number;
    mediaBase: string;
  };
};
export type ListingPatch = {
  listing?: Record<string, unknown>;
  rrp?: number | null;
  priceB2c?: number | null;
  priceB2b?: number | null;
  markupOverrideB2c?: number | null;
  markupOverrideB2b?: number | null;
  siteActionOverride?: 'show' | 'preorder' | 'hide' | null;
  images?: { id: string; is_primary?: boolean; alt?: string }[];
};
export type Publication = {
  id: string;
  partnerId: string | null;
  kind: 'listings' | 'availability' | 'archive';
  status: 'queued' | 'sent' | 'acknowledged' | 'partial' | 'failed';
  itemsCount: number;
  okCount: number;
  failedCount: number;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  items?: { sku: string; action: string | null; status: string; siteUrl: string | null; error: string | null }[];
};
export type ContractTemplate = { id: string; name: string; lang: 'en' | 'ru'; bodyMd: string; variables: { key: string; label_en: string; label_ru: string; default: unknown }[]; version: number; active: boolean; updatedAt: string };
export type Contract = {
  id: string;
  partnerId: string;
  number: string | null;
  version: number;
  status: 'Draft' | 'Sent' | 'Signed' | 'Expired';
  startDate: string | null;
  endDate: string | null;
  templateId: string | null;
  templateName: string | null;
  terms: Record<string, unknown>;
  pdfPath: string | null;
  signedAt: string | null;
  signedByName: string | null;
  signatureMethod: string | null;
  hasSignature: boolean;
  signedFile: string | null;
  sentAt: string | null;
  notes: string | null;
  createdAt: string;
  html?: string;
  markdown?: string;
  defaultTerms?: Record<string, unknown>;
};
export type SiteState = {
  siteApiUrl: string | null;
  siteMediaBase: string;
  tokenConfigured: boolean;
  schedulerEnabled: boolean;
  keys: { id: string; name: string; prefix: string; scopes: string[]; active: boolean; lastUsedAt: string | null; createdAt: string }[];
  webhooks: { id: string; url: string; events: string[]; active: boolean; lastStatus: number | null; lastDeliveryAt: string | null; lastError: string | null }[];
  categoryMap: { hubType: string; primary: string; additional: string[] }[];
  lastSnapshot: { date: string; builtAt: string; items: number } | null;
  pendingEvents: number;
  publications: Publication[];
};
export type ListingTypes = { types: string[]; map: { hubType: string; primary: string; additional: string[] }[]; colors: string[] };

export const onboardingApi = {
  partners: () => api.get<PartnerFull[]>('/partners'),
  partner: (id: string) => api.get<PartnerFull>(`/partners/${id}`),
  createPartner: (body: Record<string, unknown>) => api.post<PartnerFull>('/partners', body),
  patchPartner: (id: string, body: Record<string, unknown>) => api.patch<PartnerFull>(`/partners/${id}`, body),
  inviteOwner: (id: string, body: { email: string; fullName?: string; locale?: string }) => api.post<{ email: string; inviteUrl: string | null }>(`/partners/${id}/invite-owner`, body),
  uploadLogo: (id: string, fd: FormData) => api.upload<{ logoPath: string }>(`/partners/${id}/logo`, fd),
  listings: (id: string) => api.get<ListingsPayload>(`/partners/${id}/listings`),
  patchListing: (sku: string, body: ListingPatch) => api.patch<ListingRow>(`/listings/${encodeURIComponent(sku)}`, body),
  uploadImage: (sku: string, fd: FormData) => api.upload<ListingRow>(`/listings/${encodeURIComponent(sku)}/images`, fd),
  deleteImage: (sku: string, imageId: string) => api.del<ListingRow>(`/listings/${encodeURIComponent(sku)}/images/${imageId}`),
  siteJson: (sku: string) => api.get<SiteListing>(`/listings/${encodeURIComponent(sku)}/site-json`),
  recalc: (id: string) => api.post<{ recalculated: number }>(`/partners/${id}/listings/recalc`),
  approvals: (id: string) => api.get<Approval[]>(`/partners/${id}/approvals`),
  approve: (id: string, body: Record<string, unknown>) => api.post<Approval>(`/partners/${id}/approvals`, body),
  send: (id: string, skus: string[], force = false) => api.post<Publication>(`/partners/${id}/listings/send`, { skus, force }),
  archive: (sku: string, reason: string) => api.post<ListingRow>(`/listings/${encodeURIComponent(sku)}/archive`, { reason }),
  pushAvailability: (id: string) => api.post<Publication | { status: string }>(`/partners/${id}/availability/push`),
  publications: (id: string) => api.get<Publication[]>(`/partners/${id}/publications`),
  types: () => api.get<ListingTypes>('/listings/types'),
  templates: () => api.get<ContractTemplate[]>('/contracts/templates'),
  contracts: (id: string) => api.get<Contract[]>(`/partners/${id}/contracts`),
  createContract: (id: string, body: { templateId: string; terms: Record<string, unknown>; startDate: string; endDate: string; notes?: string }) => api.post<Contract>(`/partners/${id}/contracts`, body),
  contract: (cid: string) => api.get<Contract>(`/contracts/${cid}`),
  sendContract: (cid: string) => api.post<Contract>(`/contracts/${cid}/send`),
  signContract: (cid: string, body: { method: string; signerName: string; signature?: string }) => api.post<Contract>(`/contracts/${cid}/sign`, body),
  uploadScan: (cid: string, fd: FormData) => api.upload<Contract>(`/contracts/${cid}/scan`, fd),
  myContract: () => api.get<{ contract: Contract | null; html?: string }>('/contracts/mine'),
  siteState: () => api.get<SiteState>('/settings/site'),
  createKey: (name: string, scopes?: string[]) => api.post<{ id: string; token: string; prefix: string }>('/settings/site/keys', scopes ? { name, scopes } : { name }),
  revokeKey: (id: string) => api.del<{ ok: boolean }>(`/settings/site/keys/${id}`),
  putCategoryMap: (items: { hubType: string; primary: string; additional: string[] }[]) => api.put<SiteState>('/settings/site/category-map', { items }),
  testSite: () => api.post<{ status: number; body: string }>('/settings/site/test'),
  snapshot: () => api.post<{ date: string; items: number }>('/settings/site/snapshot'),
  deleteWebhook: (id: string) => api.del<{ ok: boolean }>(`/settings/site/webhooks/${id}`),
  putSettings: (values: Record<string, unknown>) => api.put<Record<string, unknown>>('/settings', { values }),
  getSettings: () => api.get<Record<string, unknown>>('/settings'),
};

export type SiteScanRun = { id: string; partnerId: string; url: string; status: 'queued' | 'running' | 'done' | 'failed'; pagesSeen: number; productsFound: number; matched: number; startedAt: string | null; finishedAt: string | null; error: string | null; log: string[]; createdAt: string };
export type SiteScanState = { last: SiteScanRun | null; running: boolean; products: { total: number; matched: number; applied: number } };
export type SiteProductRow = { id: string; url: string; title: string | null; siteSku: string | null; images: string[]; price: number | null; currency: string | null; availability: string | null; description: string | null; specs: Record<string, string>; matchedSku: string | null; matchedArticle: string | null; matchScore: number | null; appliedAt: string | null; fetchedAt: string };

/** Разбор сайта поставщика (29.10.2026): обход, карточки с сайта, привязка к артикулам, подстановка в листинги. */
export const siteScanApi = {
  start: (partnerId: string, url?: string) => api.post<{ scan: SiteScanRun; started: boolean }>(`/partners/${partnerId}/site-scan`, { url: url || null }),
  state: (partnerId: string) => api.get<SiteScanState>(`/partners/${partnerId}/site-scan`),
  products: (partnerId: string, matched?: boolean) => api.get<SiteProductRow[]>(`/partners/${partnerId}/site-products${matched == null ? '' : `?matched=${matched}`}`),
  setMatch: (partnerId: string, id: string, matchedArticle: string | null) => api.patch<SiteProductRow>(`/partners/${partnerId}/site-products/${id}`, { matchedArticle }),
  apply: (partnerId: string, body: { skus?: string[]; overwriteImages?: boolean; rematch?: boolean } = {}) => api.post<{ applied: number; matched: number | null }>(`/partners/${partnerId}/site-products/apply`, body),
};

export const contractPdfUrl = (cid: string, inline = false) => `/api/contracts/${cid}/pdf${inline ? '?inline=1' : ''}`;

export const PROBLEM_KEYS = ['contract_required', 'approval_required', 'excluded_by_partner', 'category_missing', 'image_missing', 'name_missing', 'price_missing', 'price_below_min', 'discontinued'] as const;
